import { InputArea } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, {
  ChangeEventHandler,
  FC,
  KeyboardEvent,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TLocalesKeys as K } from '../../../../types';
import { isMacOS, isSafari, preventEvent } from '../../../../util';
import { hasContent } from '../../../../util/guards';
import { fieldActions } from '../../../common/reducers/field';
import { selectIsLiveChat, selectLastItem } from '../../../common/selectors/chat';
import { selectFieldState } from '../../../common/selectors/field';
import { renderModel } from '../../../util/renderModel';
import { useIsRtlText } from '../../hooks';
import { DirectionWrapper } from '../DirectionWrapper';
import { FIELD_ANIMATION_DURATION } from './constants';
import { selectIsHidden } from './selectors';
import { useShouldFocusOnInput } from './useShouldFocusOnInput';
import { isEnter } from './util';

export interface InputProps {
  onSend: (actionType: string) => void;
}

export const Input: FC<InputProps> = ({ onSend }) => {
  const isMobile = renderModel.isMobile();
  const { placeholder, value, disabled, maxLength } = useSelector(selectFieldState);
  const isLiveChat = useSelector(selectIsLiveChat);
  const isHidden = useSelector(selectIsHidden);
  const inputRef = useRef<InputArea>(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const shouldFocus = useShouldFocusOnInput();
  const isDisabled = (disabled && !isLiveChat) || isHidden;
  const lastMessageItem = useSelector(selectLastItem);
  const [autoGrow, setAutoGrow] = useState(true);
  const autoGrowToogleTimeout = useRef<ReturnType<typeof setTimeout>>();

  const setFocus = useRef(() => inputRef.current?.focus());

  useEffect(() => {
    if (shouldFocus && !isDisabled) {
      // Setting focus after animation is completed, otherwise animation is broken
      const timeout = setTimeout(() => setFocus.current(), FIELD_ANIMATION_DURATION);
      return () => clearTimeout(timeout);
    }
    // Adding lastMessageItem to dependencies to check if focus required per every new message
  }, [shouldFocus, isDisabled, lastMessageItem]);

  const onChangeHandler: ChangeEventHandler<HTMLTextAreaElement> = useCallback(
    (event) => {
      if (event.target instanceof HTMLTextAreaElement) {
        dispatch(fieldActions.changeValue(event.target.value));
      }
    },
    [dispatch],
  );

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
    (event) => {
      if (isMobile || isComposingEvent(event)) {
        return;
      }

      if (isEnter(event)) {
        preventEvent(event);
        onSend('enter');
      }
    },
    [isMobile, onSend],
  );

  const hint = hasContent(placeholder)
    ? placeholder
    : t<K>(isDisabled ? 'field_placeholder_disabled' : 'field_placeholder_default');

  const isRtl = useIsRtlText(value || hint);

  useEffect(() => {
    // Refreshing extra rows that could appear after auto grow
    if (value === '') {
      // Element ref is typed as any in WDS
      const textarea = (inputRef.current as any).textArea as HTMLTextAreaElement;
      textarea?.setAttribute('rows', '1');
    } else if (document.activeElement !== (inputRef.current as any).textArea) {
      // If the input is not focused it means the value was updated externally, not from typing.
      // In this case we trigger componentDidUpdate in InputArea by toggling autoGrow so that the input height is adjusted.
      setAutoGrow(false);
      autoGrowToogleTimeout.current = setTimeout(() => setAutoGrow(true));
    }

    return () => {
      clearTimeout(autoGrowToogleTimeout.current);
    };
  }, [value]);

  return (
    <DirectionWrapper isRtl={isRtl}>
      <InputArea
        maxHeight="180px" // 7 lines max
        size="medium"
        ref={inputRef}
        onChange={onChangeHandler}
        onKeyDown={handleKeyDown}
        value={value}
        placeholder={hint}
        ariaLabel={hint}
        disabled={isDisabled}
        maxLength={maxLength}
        autoGrow={autoGrow}
        minRowsAutoGrow={1}
        dataHook="field"
      />
    </DirectionWrapper>
  );
};

// we need to skip IME events(when user clicks on enter to choose something from Input method editor)
// https://developer.mozilla.org/en-US/docs/Glossary/Input_method_editor
function isComposingEvent(event: KeyboardEvent<HTMLTextAreaElement>): boolean {
  if (isMacOS() && isSafari) {
    // behaviour of `isComposing` for safari(macOs version) is different(events order between "compositionend" and "input" events).
    // To unify it for all browsers it needs to use 'which' and check is it IME(https://github.com/w3c/uievents/issues/202)
    // according to W3C "If an Input Method Editor is processing key input and the event is keydown, return 229"(https://www.w3.org/TR/uievents/#determine-keydown-keyup-keyCode)
    const isProcessingIME = event.which === 229;
    return event.nativeEvent.isComposing || isProcessingIME;
  }

  return event.nativeEvent.isComposing;
}
